import { useEffect, useMemo } from 'react';
import { ShieldCheck } from 'lucide-react';
import { Tooltip } from '@/Components/ui/Tooltip';
import { ToggleInput } from '@/Components/shared/ToggleInput';
import { useAssistanceFormulas, useAssistancePrices } from '@/features/assistance/hooks/useAssistance';
import { isAssistanceEligibleDuration } from '@/features/assistance/types/assistance.types';

/**
 * Carte du bloc, portée par le COMPOSANT et non par ses points d'appel.
 *
 * Même motif que les blocs voisins de l'étape 2 de l'assistant. Elle vivait
 * jusqu'ici dans une prop `className` que seule la création passait : la
 * modification rendait donc le même contenu à nu sur le blanc de sa modale, et
 * l'encart des prestations — fond `affinicar-lighter` — y ressortait comme un
 * composant étranger au lieu de se fondre dans un bloc cohérent.
 *
 * Posée ici plutôt qu'en conteneur autour des appels : le composant sort par
 * `return null` quand l'offre ne s'applique pas, et un conteneur extérieur
 * afficherait alors une carte vide.
 */
const CARTE = 'tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey';

interface AssistanceFormulaPickerProps {
  /** Société dont on résout les tarifs — l'émettrice de la garantie. */
  societyId?: string;
  /** Durée en cours d'édition : pilote l'éligibilité ET le tarif affiché. */
  duration?: number;
  /**
   * L'offre est-elle activée sur le compte du vendeur ?
   *
   * `undefined` signifie ENCORE INCONNU — la configuration effective est en cours de
   * chargement. À distinguer de « désactivée » : l'effet d'effacement ci-dessous ne
   * doit se déclencher que sur le second.
   *
   * LE NOMBRE EST VOLONTAIRE DANS CE TYPE. L'API sert un `tinyint` sans transformer,
   * donc `1` ou `0`, jamais un vrai booléen — comme les 23 autres champs booléens de
   * la configuration effective, dont le type annonce pourtant `boolean`. Déclarer la
   * vérité ici est ce qui empêche de réintroduire le bug : la valeur brute avait
   * atteint l'option `enabled` d'un `useQuery`, que React Query refuse si elle n'est
   * pas booléenne, et tout l'écran tombait — mais seulement pour un vendeur qui A
   * l'option, donc exactement quand on voulait ajouter de l'assistance.
   */
  assistanceEnabled: boolean | number | undefined;
  value: number | null;
  onChange: (formulaId: number | null) => void;
  /**
   * `create` (défaut) : rien n'est encore enregistré — une formule devenue
   * inapplicable est effacée, sans quoi la création échouerait à la toute fin sur
   * un champ devenu invisible.
   *
   * `edit` : la garantie existe. Une formule qu'elle porte n'est JAMAIS effacée
   * d'office, conformément au serveur (`WarrantiesService.update`) : un compte qui
   * perd l'option conserve les formules de ses garanties, et une durée inéligible
   * est refusée — pas corrigée en silence, l'option étant facturée.
   */
  mode?: 'create' | 'edit';
  /** En modification : la formule enregistrée sur la garantie, pour pouvoir la restaurer. */
  persistedFormulaId?: number | null;
  /**
   * Classes ajoutées à la carte, pour un ajustement d'espacement propre à un écran.
   * N'a plus à porter l'identité visuelle du bloc : celle-ci est dans `CARTE`, donc
   * commune à tous les points d'appel par construction.
   */
  className?: string;
}

/**
 * Choix de la formule d'assistance d'une garantie.
 *
 * Partagé entre la CRÉATION (étape 2 de l'assistant) et la MODIFICATION (fiche
 * garantie), qui présentaient jusqu'ici deux interfaces différentes pour le même
 * réglage : cartes détaillées d'un côté, simple liste déroulante de l'autre — sans
 * tarif ni prestations, et sans contrôle de la durée éligible. Un seul composant
 * rend cette divergence impossible à réintroduire.
 *
 * Ne rend rien quand l'offre ne s'applique pas : compte vendeur sans assistance, ou
 * durée hors de la plage 6-60 mois.
 */
export function AssistanceFormulaPicker({
  societyId,
  duration,
  assistanceEnabled,
  value,
  onChange,
  mode = 'create',
  persistedFormulaId = null,
  className = '',
}: AssistanceFormulaPickerProps) {
  const isEdit = mode === 'edit';
  const isDurationEligible = isAssistanceEligibleDuration(duration);

  /**
   * Normalisation en UN SEUL POINT, aussitôt à l'entrée. Le reste du composant ne
   * voit plus jamais la valeur brute de l'API : trois états explicites, et rien qui
   * puisse propager un nombre dans l'option `enabled` d'un `useQuery`.
   */
  const isEnabled: boolean | undefined =
    assistanceEnabled === undefined ? undefined : !!assistanceEnabled;
  /** Tant que la configuration n'est pas revenue, on ne sait rien — on n'efface rien. */
  const isConfigurationKnown = isEnabled !== undefined;
  const isApplicable = isEnabled === true && isDurationEligible;

  // En modification, une formule présente ou enregistrée reste visible quand l'offre
  // ne s'applique plus : c'est le seul moyen de la retirer VOLONTAIREMENT.
  const lockedFormulaId = isEdit && !isApplicable ? (value ?? persistedFormulaId) : null;

  // Le référentiel est aussi nécessaire pour nommer une formule conservée sur un
  // compte qui a perdu l'option.
  const { data: formulas } = useAssistanceFormulas(
    isEnabled === true || lockedFormulaId != null,
  );
  const { data: prices } = useAssistancePrices(societyId, duration, isApplicable);

  const priceByFormulaId = useMemo(() => {
    const map = new Map<number, number>();
    (prices || []).forEach((p) => map.set(p.formulaId, p.price));
    return map;
  }, [prices]);

  const standardFormula = useMemo(
    () => (formulas || []).find((f) => f.tier === 'standard') || null,
    [formulas],
  );

  const selectedFormula = useMemo(
    () => (formulas || []).find((f) => f.id === value) || null,
    [formulas, value],
  );

  /**
   * Seul effet du composant, et CRÉATION SEULEMENT : la durée et le réglage du
   * compte changent en dehors d'ici, et une formule qui survivrait à leur bascule
   * ferait échouer la création sur un champ invisible.
   *
   * Jamais en modification : l'effet y retirait la formule d'une garantie dès
   * l'ouverture du formulaire sur un compte désactivé, et le moindre enregistrement
   * — une limite kilométrique — supprimait alors une assistance facturée.
   *
   * INERTE TANT QUE LA CONFIGURATION N'EST PAS CONNUE. L'assistant restaure son
   * brouillon depuis le stockage de session pendant quatre heures ; au remontage, la
   * configuration effective repart en chargement et vaut `undefined`. L'effet
   * lisait alors « offre désactivée » et effaçait la formule que le vendeur venait
   * de choisir, sans que rien ne la remette.
   */
  useEffect(() => {
    if (!isConfigurationKnown) return;
    if (!isEdit && !isApplicable && value != null) {
      onChange(null);
    }
  }, [isConfigurationKnown, isEdit, isApplicable, value, onChange]);

  if (!isApplicable) {
    if (lockedFormulaId == null) return null;

    const lockedLabel =
      (formulas || []).find((f) => f.id === lockedFormulaId)?.label ?? "la formule d'assistance";
    const isKept = value != null;

    return (
      <div className={`${CARTE} tw-space-y-3 ${className}`}>
        <p className="tw-text-sm tw-text-gray-500 tw-mb-0">Offre d'assistance</p>

        <div id="assistance-toggle">
          <ToggleInput
            label={`Conserver ${lockedLabel}`}
            value={isKept}
            // Restaurer ne peut rendre que la formule ENREGISTRÉE : le serveur interdit
            // tout changement de formule sur un compte qui n'a plus l'option.
            onChange={(keep) => onChange(keep ? persistedFormulaId : null)}
            dataTestId="toggle-assistance"
          />
        </div>

        {!isKept ? (
          <p className="tw-text-xs tw-text-gray-600 tw-mb-0">
            L'assistance sera retirée de cette garantie à l'enregistrement.
          </p>
        ) : !isEnabled ? (
          <p className="tw-text-xs tw-text-gray-600 tw-mb-0">
            L'offre d'assistance n'est plus active sur ce compte : la formule est conservée,
            mais ne peut plus être changée.
          </p>
        ) : (
          <p className="tw-text-xs tw-text-amber-700 tw-mb-0">
            L'assistance n'est disponible que pour les garanties de 6 à 60 mois. Retirez-la
            avant de passer cette garantie à {duration} mois.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={`${CARTE} tw-space-y-4 ${className}`}>
      <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
        Proposer l'offre d'assistance sur cette garantie
        <Tooltip content="Dépannage, remorquage, réparations sur place, rapatriement, taxi de liaison, et véhicule de prêt selon la formule. Le supplément s'ajoute au prix de la prestation Affinicar." />
      </p>

      <div id="assistance-toggle">
        <ToggleInput
          label="Offre d'assistance"
          value={value != null}
          onChange={(enabled) =>
            // Activation : on présélectionne Standard, la formule la plus simple,
            // plutôt que de laisser un état « activé sans formule » que le serveur
            // refuserait.
            onChange(enabled ? (standardFormula?.id ?? null) : null)
          }
          dataTestId="toggle-assistance"
        />
      </div>

      {value != null && (
        <div className="tw-space-y-3">
          <p className="tw-text-sm tw-text-gray-500 tw-mb-0">Choisissez la formule</p>

          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-2">
            {(formulas || []).map((formula) => {
              const price = priceByFormulaId.get(formula.id);
              const isSelected = value === formula.id;
              return (
                <button
                  key={formula.id}
                  type="button"
                  onClick={() => onChange(formula.id)}
                  className={`tw-text-left tw-rounded-lg tw-border-2 tw-px-4 tw-py-3 tw-transition-colors ${
                    isSelected
                      ? 'tw-border-affinicar-primary tw-bg-white'
                      : 'tw-border-gray-200 tw-bg-white hover:tw-border-gray-300'
                  }`}
                >
                  <span className="tw-block tw-text-sm tw-font-semibold tw-text-gray-900">
                    {formula.label}
                  </span>
                  <span className="tw-block tw-text-xs tw-text-gray-500 tw-mt-0.5">
                    {price !== undefined ? `+ ${price.toFixed(2)} € HT` : 'Tarif indisponible'}
                  </span>
                </button>
              );
            })}
          </div>

          {/*
            Récapitulatif des prestations. Présenté en encart plutôt qu'en liste de
            texte gris : c'est ce que le vendeur lit pour justifier le supplément au
            client, et ça se noyait sous le reste du formulaire. Les montants sont
            alignés à droite, en gras — c'est l'information qu'on cherche du regard.
            Volontairement compact : un encart de la hauteur des cartes du dessus
            déséquilibrerait une étape qui porte déjà beaucoup de réglages.
          */}
          {selectedFormula?.benefits?.blocks && (
            <div className="tw-rounded-lg tw-border-l-2 tw-border-affinicar-primary tw-bg-affinicar-lighter tw-px-3 tw-py-2.5">
              {/* Teinte secondaire et non primary : sur `affinicar-lighter`, le violet
                  de base ne contraste qu'à 3,3:1, sous le seuil lisible en 12 px. */}
              <p className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-affinicar-secondary tw-mb-1.5">
                <ShieldCheck size={14} className="tw-flex-shrink-0" />
                Inclus dans cette formule
              </p>

              <dl className="tw-space-y-1 tw-mb-0">
                {selectedFormula.benefits.blocks.map((block) => (
                  <div
                    key={block.label}
                    className="tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-text-xs"
                  >
                    <dt className="tw-font-normal tw-text-gray-600">{block.label}</dt>
                    <dd className="tw-font-semibold tw-text-gray-900 tw-text-right tw-mb-0">
                      {block.value}
                    </dd>
                  </div>
                ))}
                {selectedFormula.benefits.loaner && (
                  <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-text-xs">
                    <dt className="tw-font-normal tw-text-gray-600">
                      Véhicule de remplacement
                    </dt>
                    <dd className="tw-font-semibold tw-text-gray-900 tw-text-right tw-mb-0">
                      {selectedFormula.benefits.loaner}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
